import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { IMobileFlow, useOfferMode } from "@nju/data-access-portal-offers";
import { IMobileOrchestrator } from "@nju/data-access-portal-orchestrator";
import { pushSimpleEvent } from "@nju/data-layer";
import { portalRoutes } from "@nju/routes";
import { A, Separator } from "@nju/ui";
import { Button } from "../../OrderCTABanner/Pressable";
import { getAvailableActions } from "../../utils";

interface IButtonsProps {
  activateOrder: () => void;
  isActivateOrderLoading: boolean;
  cancelOrder: () => void;
  isCancelOrderLoading: boolean;
  orchestrator: IMobileOrchestrator;
  refreshCallback: () => void;
  shippingStatus: IMobileOrchestrator["shippingStatus"];
  retryPaymentCallback: () => void;
  paymentErrorCategory: IMobileOrchestrator["paymentErrorCategory"];
  paymentStatus: IMobileOrchestrator["paymentStatus"];
  cancellationReason: IMobileOrchestrator["cancellationReason"];
  userId: string;
  flow: IMobileFlow | undefined;
}

export function Buttons({
  activateOrder,
  isActivateOrderLoading,
  cancelOrder,
  isCancelOrderLoading,
  orchestrator,
  refreshCallback,
  shippingStatus,
  paymentErrorCategory,
  retryPaymentCallback,
  cancellationReason,
  userId,
  flow,
}: IButtonsProps) {
  const router = useRouter();
  const mode = useOfferMode();
  const {
    identityMethodId,
    isUserVerified,
    state,
    actions,
    waybillNumber,
    processId,
    mnp,
  } = orchestrator;
  const { SIM_SET, ORDER_ACTIVATE } = getAvailableActions(actions);

  const handleAddSimCard = React.useCallback(() => {
    router.push(
      portalRoutes.mojeKonto.dodajKarteSim.get({
        mode,
        ...(userId && { userId }),
      }),
    );
  }, [mode, router, userId]);

  const handleOpenDPDLink = React.useCallback(() => {
    window.open(`https://tt.dpd.com.pl/${waybillNumber}`, "_blank");
  }, [waybillNumber]);

  const handleSetPaymentMethod = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_payment_complete_order",
    });

    router.push(
      portalRoutes.mojeKonto.platnosc.index.get({
        mode,
        userId,
      }),
    );
  }, [mode, router, userId]);

  const handleCancelOrderForm = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_resignation_complete_order",
    });

    router.push(
      portalRoutes.mojeKonto.anulujZamowienie.get({
        mode,
        ...(userId && { userId }),
        ...(flow && { flow }),
      }),
    );
  }, [router, mode, userId, flow]);

  if (state === "ORDER_PAYMENT_ERROR" && paymentErrorCategory) {
    return (
      <>
        <Separator size="xs" tone="invisible" />
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Link
            href={portalRoutes.mojeKonto.platnosci.index.get()}
            passHref={true}
          >
            <A
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
              variant="secondary"
            >
              zarządzaj płatnością
            </A>
          </Link>
          <Button
            isPrimary={true}
            onPress={retryPaymentCallback}
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
          >
            ponów płatność
          </Button>
        </div>
      </>
    );
  }

  if (state === "SIM" && processId !== "MNP_IN") {
    return (
      <>
        {shippingStatus && <Separator size="xs" tone="invisible" />}
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          {(shippingStatus === "DELV_CHAN" ||
            shippingStatus === "DELV_PROGR_CHAN" ||
            shippingStatus === "PACK_CHAN") &&
            SIM_SET && (
              <Button
                data-cy="add-sim-button"
                id="button_nm_dodaj_karte_sim"
                isPrimary={shippingStatus === "DELV_CHAN"}
                onPress={handleAddSimCard}
              >
                dodaj kartę SIM
              </Button>
            )}

          {(shippingStatus === "PACK_CHAN" ||
            shippingStatus === "DELV_PROGR_CHAN") && (
            <Button
              id="button_nm_sledz_zamowienie"
              isPrimary={true}
              onPress={handleOpenDPDLink}
            >
              śledź zamówienie
            </Button>
          )}

          {(shippingStatus === "RET_CHAN" ||
            shippingStatus === "RET_PROGR_CHAN") && (
            <Button isPrimary={true} onPress={refreshCallback}>
              odśwież
            </Button>
          )}
        </div>
      </>
    );
  }

  if (state === "PAYMENT_METHOD") {
    return (
      <>
        <Separator size="xs" tone="invisible" />
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Button isPrimary={true} onPress={handleSetPaymentMethod}>
            wybierz sposób płatności
          </Button>
        </div>
      </>
    );
  }

  if (
    processId !== "MNP_IN" &&
    (state === "ORDER_FINISHING" ||
      state === "ORDER_COMMITTED" ||
      state === "COMPLETED" ||
      (state === "ACTIVATION" &&
        !isUserVerified &&
        identityMethodId === "TECHNICALPARTNER"))
  ) {
    return (
      <>
        <Separator size="xs" tone="invisible" />
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Button isPrimary={true} onPress={refreshCallback}>
            odśwież
          </Button>
        </div>
      </>
    );
  }

  if (state === "ACTIVATION") {
    return (
      <>
        <Separator size="xs" tone="invisible" />
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Button
            isDisabled={!ORDER_ACTIVATE}
            isLoading={isActivateOrderLoading}
            isPrimary={true}
            onPress={activateOrder}
          >
            aktywuj
          </Button>
        </div>
      </>
    );
  }

  if (
    state === "ORDER_PENDING_CANCELLATION" &&
    cancellationReason === "agentRequest"
  ) {
    return (
      <>
        <Separator size="xs" tone="invisible" />
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Button isPrimary={true} onPress={refreshCallback}>
            odśwież
          </Button>
        </div>
      </>
    );
  }

  if (processId === "MNP_IN" && state === "SIM") {
    if (orchestrator.shippingStatus === "PACK_CHAN") {
      return (
        <>
          <Separator size="xs" tone="invisible" />
          <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
            <Button id="button_nm_dodaj_karte_sim" onPress={handleAddSimCard}>
              dodaj kartę SIM
            </Button>
            <Button
              id="button_nm_sledz_zamowienie"
              isPrimary={true}
              onPress={handleOpenDPDLink}
            >
              śledź zamówienie
            </Button>
          </div>
        </>
      );
    }

    if (orchestrator.shippingStatus === "DELV_PROGR_CHAN") {
      return (
        <>
          <Separator size="xs" tone="invisible" />
          <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
            <Button id="button_nm_dodaj_karte_sim" onPress={handleAddSimCard}>
              dodaj kartę SIM
            </Button>
            <Button
              id="button_nm_sledz_zamowienie"
              isPrimary={true}
              onPress={handleOpenDPDLink}
            >
              śledź zamówienie
            </Button>
          </div>
        </>
      );
    }

    if (orchestrator.shippingStatus === "DELV_CHAN") {
      return (
        <>
          <Separator size="xs" tone="invisible" />
          <Button
            data-cy="add-sim-button-2"
            id="button_nm_dodaj_karte_sim"
            isPrimary={true}
            onPress={handleAddSimCard}
          >
            dodaj kartę SIM
          </Button>
        </>
      );
    }
  }

  if (state === "ORDER_FULFILLMENT_FAILED" && processId !== "MNP_IN") {
    return (
      <>
        <Separator size="xs" tone="invisible" />
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Button isPrimary={true} onPress={handleCancelOrderForm}>
            anuluj zamówienie
          </Button>
        </div>
      </>
    );
  }

  if (state === "ORDER_FULFILLMENT_FAILED" && processId === "MNP_IN") {
    if (
      (mnp?.reasonCode === "1" ||
        mnp?.reasonCode === "2" ||
        mnp?.reasonCode === "4" ||
        mnp?.reasonCode === "6" ||
        mnp?.reasonCode === "8") &&
      mnp?.state !== "Failed"
    ) {
      return (
        <>
          <Separator size="xs" tone="invisible" />
          <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
            <Button
              isPrimary={true}
              onPress={handleCancelOrderForm}
              testId="mv-cancel-order-button"
            >
              anuluj obecne zamówienie
            </Button>
          </div>
        </>
      );
    }

    if (
      (mnp?.reasonCode !== "1" &&
        mnp?.reasonCode !== "2" &&
        mnp?.reasonCode !== "4" &&
        mnp?.reasonCode !== "6" &&
        mnp?.reasonCode !== "8" &&
        mnp?.reasonCode) ||
      mnp?.state === "Failed"
    ) {
      return (
        <>
          <Separator size="xs" tone="invisible" />
          <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
            <Button
              id="button_nm_zloz_nowe_zamowienie"
              isLoading={isCancelOrderLoading}
              isPrimary={true}
              onPress={cancelOrder}
              testId="mv-place-new-order-button"
            >
              złóż nowe zamówienie
            </Button>
          </div>
        </>
      );
    }

    if (
      mnp?.npMessageCode !== "E16" &&
      mnp?.npMessageCode !== "E17" &&
      (shippingStatus === "RET_CHAN" ||
        shippingStatus === "RET_PROGR_CHAN" ||
        shippingStatus === "CANC_CHAN")
    ) {
      return (
        <>
          <Separator size="xs" tone="invisible" />
          <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
            <Button isPrimary={true} onPress={handleCancelOrderForm}>
              anuluj zamówienie
            </Button>
          </div>
        </>
      );
    }
  }

  if (
    (state === "ORDER_FINISHING" || state === "COMPLETED") &&
    processId === "MNP_IN"
  ) {
    return (
      <>
        <Separator size="xs" tone="invisible" />
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Button isPrimary={true} onPress={refreshCallback}>
            odśwież
          </Button>
        </div>
      </>
    );
  }

  // eslint-disable-next-line unicorn/no-null
  return null;
}
